// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngCarousel from "@sc/App/Components/XngCarousel";
import LazyImage, { LazyImageProps } from "@sc/MobiusOverrides/LazyImage";
import LazyPicture from "@sc/MobiusOverrides/LazyImage/LazyPicture";
import {
    SLIDES_PER_VIEW_POINTS,
    TESTIMONIALS_CAROUSEL_SLIDES_PER_SCREENSIZE,
    XNGAGE_CUSTOM_WIDGET,
} from "@sc/XDL/constants";
import { generateSlidesPerViewMap } from "@sc/XDL/Utilities";
import React from "react";

const enum fields {
    testimonials = "testimonials",
}

const enum testimonialFields {
    copy = "copy",
    author = "author",
    company = "company",
    desktopImage = "desktopImage",
    tabletImage = "tabletImage",
    mobileImage = "mobileImage",
}

interface TestimonialModel {
    fields: {
        copy: string;
        author: string;
        company: string;
        desktopImage: string;
        tabletImage: string;
        mobileImage: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.testimonials]: TestimonialModel[];
    };
}

type Props = OwnProps;

export interface TestimonialsStyles {
    container?: InjectableCss;
    testimonialContainer?: InjectableCss;
    copyText?: TypographyProps;
    authorText?: TypographyProps;
    companyName?: TypographyProps;
    separator?: InjectableCss;
    autherCompanyWrapper?: InjectableCss;
    dataWrapper?: InjectableCss;
    image?: LazyImageProps;
    dataSeparator?: InjectableCss;
}

export const testimonialsStyles: TestimonialsStyles = {};

const styles = testimonialsStyles;

const Testimonials = ({ id, fields: { testimonials } }: Props) => {
    const breakpoints = generateSlidesPerViewMap({
        [SLIDES_PER_VIEW_POINTS.xs]: TESTIMONIALS_CAROUSEL_SLIDES_PER_SCREENSIZE.mobile,
        [SLIDES_PER_VIEW_POINTS.sm]: TESTIMONIALS_CAROUSEL_SLIDES_PER_SCREENSIZE.tablet,
        [SLIDES_PER_VIEW_POINTS.md]: TESTIMONIALS_CAROUSEL_SLIDES_PER_SCREENSIZE.desktop,
        [SLIDES_PER_VIEW_POINTS.lg]: TESTIMONIALS_CAROUSEL_SLIDES_PER_SCREENSIZE.desktop,
        [SLIDES_PER_VIEW_POINTS.xl]: TESTIMONIALS_CAROUSEL_SLIDES_PER_SCREENSIZE.desktop,
    });

    const renderTestimonials = testimonials.map((testimonial, index) => {
        const { copy, author, company, desktopImage, tabletImage, mobileImage } = testimonial.fields;

        const image = desktopImage || tabletImage || mobileImage;
        return (
            <StyledWrapper
                key={`${author}_${index}`}
                {...styles.testimonialContainer}
                data-test-selector={`testimonial-of-company-${company}-by-${author}`}
            >
                {image  && (
                    <LazyPicture
                        {...styles.image}
                        largeImageSrc={desktopImage || tabletImage || mobileImage}
                        smallImageSrc={mobileImage || desktopImage || tabletImage}
                        mediumImageSrc={tabletImage || desktopImage || mobileImage}
                        altText={`testimonial-of-${company}-by-${author}`}
                    />
                )}
                <StyledWrapper {...styles.dataWrapper} {...{ image }}>
                    <Typography {...styles.copyText}>{copy}</Typography>
                    <StyledWrapper>
                        <StyledWrapper {...styles.dataSeparator} />
                        <StyledWrapper {...styles.autherCompanyWrapper}>
                            <Typography {...styles.authorText}>{author}</Typography>
                            <StyledWrapper {...styles.separator} />
                            <Typography {...styles.companyName}>{company}</Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        );
    });

    return (
        <StyledWrapper {...styles.container}>
            <XngCarousel
                scrollbar={{ draggable: true, el: ".swiper-scrollbar" }}
                breakpoints={breakpoints}
                spaceBetween={22}
                navigation={true}
                pagination={false}
            >
                {renderTestimonials}
            </XngCarousel>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: Testimonials,
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
        icon: "list-ul",
        isSystem: true,
        fieldDefinitions: [
            {
                name: fields.testimonials,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.author;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: testimonialFields.desktopImage,
                        displayName: "Desktop Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        tooltip: translate("preferred dimensions 656*244"),
                    },
                    {
                        name: testimonialFields.tabletImage,
                        displayName: "Tablet Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        tooltip: translate("preferred image width 992px"),
                    },
                    {
                        name: testimonialFields.mobileImage,
                        displayName: "Mobile Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        tooltip: translate("preferred image width 735px"),
                    },
                    {
                        name: testimonialFields.copy,
                        displayName: translate("Copy"),
                        editorTemplate: "MultilineTextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: testimonialFields.author,
                        displayName: translate("Author Name"),
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: testimonialFields.company,
                        displayName: translate("Company Name"),
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
